// Pure helpers for the Superadmin "Transcription provider" UI. No imports so they run under `deno test`.

export type TranscriptionPresetKey = 'groq' | 'openai' | 'custom';

export interface TranscriptionModelOption { id: string; label: string; note: string }

export const TRANSCRIPTION_PRESETS: Record<
  TranscriptionPresetKey,
  { label: string; baseUrl: string; models: TranscriptionModelOption[] }
> = {
  groq: {
    label: 'Groq Whisper',
    baseUrl: 'https://api.groq.com/openai/v1',
    models: [
      { id: 'whisper-large-v3-turbo', label: 'whisper-large-v3-turbo', note: 'Fastest, multilingual' },
      { id: 'whisper-large-v3', label: 'whisper-large-v3', note: 'Most accurate, multilingual incl. Arabic' },
    ],
  },
  openai: {
    label: 'OpenAI Whisper',
    baseUrl: 'https://api.openai.com/v1',
    models: [{ id: 'whisper-1', label: 'whisper-1', note: 'OpenAI hosted Whisper' }],
  },
  custom: { label: 'Custom OpenAI-compatible', baseUrl: '', models: [] },
};

export const CUSTOM_TRANSCRIPTION_NOTE =
  'The endpoint must implement POST {base}/audio/transcriptions (OpenAI-compatible) and return verbose_json with segments. A tenant\'s capture policy must list this provider ("allowed ASR providers") before it is used.';

export interface TranscriptionProvider {
  id: string;
  label: string;
  preset: string;
  base_url: string;
  default_model: string;
  api_key_last4: string | null;
  is_active: boolean;
  health_status: 'unknown' | 'healthy' | 'unhealthy';
  health_error: string | null;
  health_checked_at: string | null;
}

export interface TranscriptionTestResult {
  ok: boolean;
  httpStatus?: number;
  latencyMs?: number;
  error?: string;
  errorCode?: string;
  testToken?: string;
}

/** What the test was run against. The apiKey is only held in memory to detect edits. */
export interface TestedConfig { baseUrl: string; model: string; apiKey: string }

export function sameConfig(a: TestedConfig, b: TestedConfig): boolean {
  return a.baseUrl.trim() === b.baseUrl.trim() && a.model.trim() === b.model.trim() && a.apiKey === b.apiKey;
}

export const KEY_REENTRY_HELP = 'Re-enter the API key to use a different base URL';

/** In edit mode, pointing the stored key at a different host requires typing a new key. */
export function requiresKeyReentry(o: { isEdit: boolean; baseUrl: string; storedBaseUrl?: string; apiKey: string }): boolean {
  if (!o.isEdit || o.storedBaseUrl === undefined) return false;
  return o.baseUrl.trim() !== o.storedBaseUrl.trim() && o.apiKey.length === 0;
}

export function canTestTranscription(o: { baseUrl: string; model: string; apiKey: string; isEdit: boolean; storedBaseUrl?: string }): boolean {
  if (!o.baseUrl.trim() || !o.model.trim()) return false;
  if (requiresKeyReentry(o)) return false;
  return o.isEdit || o.apiKey.length > 0;
}

export function canSaveTranscription(o: {
  label: string; test: { result: TranscriptionTestResult; config: TestedConfig } | null; current: TestedConfig;
  saving: boolean; testing: boolean; isEdit: boolean; storedBaseUrl?: string;
}): boolean {
  if (o.saving || o.testing || o.label.trim().length === 0) return false;
  if (requiresKeyReentry({ isEdit: o.isEdit, baseUrl: o.current.baseUrl, storedBaseUrl: o.storedBaseUrl, apiKey: o.current.apiKey })) return false;
  return !!o.test && o.test.result.ok === true && !!o.test.result.testToken && sameConfig(o.test.config, o.current);
}

export function describeTranscriptionTest(r: TranscriptionTestResult): { ok: boolean; title: string; detail?: string; raw?: string } {
  if (r.ok) {
    return {
      ok: true,
      title: 'Passed: the endpoint accepted audio',
      detail: r.latencyMs != null ? `${r.latencyMs} ms` : undefined,
    };
  }
  switch (r.errorCode) {
    case 'http_error':
      if (r.httpStatus === 401 || r.httpStatus === 403) {
        return { ok: false, title: 'Authentication failed', detail: `The provider rejected the API key (HTTP ${r.httpStatus}). Check the key and that it can use speech-to-text.` };
      }
      if (r.httpStatus === 404) {
        return { ok: false, title: 'Endpoint or model not found', detail: 'Check the base URL (it should end in /v1) and the model name.', raw: r.error };
      }
      if (r.httpStatus === 429) {
        return { ok: false, title: 'Rate limited or out of quota', detail: 'The provider returned HTTP 429. Try again shortly or check the account quota.' };
      }
      return { ok: false, title: `Provider returned HTTP ${r.httpStatus ?? 'error'}`, raw: r.error };
    case 'unreachable':
      return { ok: false, title: 'Could not reach the provider', detail: 'Network error: check the base URL and that the endpoint is reachable from the server.' };
    case 'bad_response':
      return { ok: false, title: 'Unexpected response', detail: 'The endpoint did not return an OpenAI-style transcription response.' };
    default:
      return { ok: false, title: 'Test failed', raw: r.error };
  }
}

/** Message for an error returned by save / activate / deactivate / delete. */
export function describeTranscriptionError(code: string | undefined, message: string | undefined): string {
  switch (code) {
    case 'test_required': return 'The connection test is missing or out of date. Run Test again for the current URL, model and key, then save.';
    case 'key_reentry_required': return KEY_REENTRY_HELP;
    case 'kind_mismatch': return 'That provider is not a transcription provider.';
    case 'platform_only': return 'Transcription providers are platform-wide only.';
    case 'invalid_base_url': return message && message.length > 0 ? message : 'The base URL is not allowed.';
    default: return message && message.length > 0 ? message : 'Something went wrong. Please try again.';
  }
}

export function canActivateTranscription(p: TranscriptionProvider): boolean {
  return !p.is_active && p.health_status === 'healthy';
}

export function activateBlockedReason(p: TranscriptionProvider): string | null {
  if (p.is_active) return 'Already active';
  return canActivateTranscription(p) ? null : 'Run Test first: activation requires a passing transcription test';
}

export function healthText(p: TranscriptionProvider): string {
  return p.health_status === 'healthy' ? 'healthy' : p.health_status === 'unhealthy' ? 'unhealthy' : 'not tested';
}

export const NO_ACTIVE_BANNER =
  'No transcription provider is active. Transcription falls back to the TRANSCRIPTION_* / LLM_API_KEY environment variables. ' +
  'The UI cannot see whether those are set; if they are not, meeting capture audio and the Ask Masira mic will report that voice input is not set up.';

/**
 * Classifies a 2xx response body. llm-providers returns a FAILED connection test as HTTP 200
 * with `{ ok: false, error, errorCode, ... }`; that is a test result to display, not an API error.
 * Returns an error descriptor only for a truthy `error` on a body that is not a test result.
 */
export function bodyError(data: unknown): { code?: string; message: string } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as { error?: unknown; code?: unknown; ok?: unknown };
  if (!d.error || typeof d.ok === 'boolean') return null;
  return typeof d.code === 'string' ? { code: d.code, message: String(d.error) } : { message: String(d.error) };
}
